<script setup lang="ts">
import debounce from "lodash-es/debounce";
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";

import { toast } from "@/common/utils/toast";
import { valueToCurrency } from "@/common/utils/number";
import { getDate } from "@/common/utils/dates";

import { DefaultTheme } from "@/common/themes";

import {
  baseAccordion,
  baseConfirm,
  baseInput,
  baseTextarea,
  svgIcon,
} from "@/common/components";

import {
  userInvoices,
  userAccount,
  userPatchAccount,
  userAccountByMember,
} from "@/common/api/rest/user";
import { planList } from "@/common/api/rest/plan";

import type { UserAccountType } from "@/common/types/user";
import type { PlanType } from "@/common/types/plan/plan";
import store from "@/store";

type AccordionType = keyof typeof accordions.value;

const { t } = useI18n();

const accordionsDefault = {
  account: false,
  signature: false,
  invoices: false,
  plans: false,
};

const accordions = ref({ ...accordionsDefault });

const account = ref<UserAccountType | null>(null);
const signature = ref<any>(null);

const plans = ref<PlanType[]>([]);
const invoices = ref([]);
const selectedPlan = ref<PlanType>();

const accountPayload = ref({ name: "", description: null });

const showBuyPlan = ref(false);

const frequeciesEnum = computed(() => ({
  MONTHLY: "Mensal",
  YEARLY: "Anual",
}));

const typeEnum = computed(() => ({
  PLAN: "Plano",
  AFFILIATE: "Afiliado",
}));

const statusEnum = computed(() => ({
  ACTIVE: "Ativo",
  CANCELED: "Cancelado",
  EXPIRED: "Vencido",
  INACTIVE: "Inativo",
  PAST_DUE: "Atrasado",
  PAID: "Pago",
  PENDING: "Pendente",
  REFUNDED: "Reembolsado",
}));

function fetchPlans() {
  if (!account.value) return;

  planList(account.value?.account_id)
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        plans.value = data.list;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function fetchInvoices() {
  if (!account.value) return;

  userInvoices(account.value.account_id)
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        invoices.value = data.list;
      } else {
        toast({ message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

async function fetchAccount() {
  if (!store.getters.user) return;

  await userAccountByMember(store.getters.user?.member_id)
    .then((response) => {
      const { success, data, message } = response;
      if (success) {
        account.value = data;
        accountPayload.value = {
          name: data.name || null,
          description: data.description || null,
        };
        signature.value = data.plans?.length ? data.plans[0] : null;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function patchAccount() {
  if (!account.value) return;

  userPatchAccount(account.value.account_id, accountPayload.value)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        toast({ message, type: "success" });
        fetchAccount();
      } else {
        toast({ message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function cancelBuyPlan() {
  showBuyPlan.value = false;
  selectedPlan.value = undefined;
}

function toggleAccordion(type: AccordionType, value: boolean) {
  accordions.value = { ...accordionsDefault };

  accordions.value[type] = value;
}

function getPrice(price: number) {
  if (price) {
    return (price / 100).toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      style: "currency",
      currency: "BRL",
    });
  }

  return " -- ";
}

const update = debounce(patchAccount, 1000);

onMounted(async () => {
  accordions.value.account = true;
  await fetchAccount();
  fetchPlans();
  fetchInvoices();
});
</script>

<template>
  <default-theme menu-active="signs" :account-status="account?.status">
    <section class="plans">
      <base-accordion
        :open="accordions.account"
        @open="toggleAccordion('account', $event)"
      >
        <template #icon>
          <svg-icon icon="card-2" size="2rem" />
        </template>
        <template #title>
          {{ t("plan.labels.my_account").toUpperCase() }}
        </template>
        <template #content>
          <div class="account">
            <template v-if="account">
              <div class="info">
                <base-input
                  v-model="accountPayload.name"
                  :value="accountPayload.name"
                  :placeholder="t('common.placeholders.name')"
                  :required="true"
                  @input="update()"
                >
                  <template #label>
                    {{ t("common.labels.name") }}
                  </template>
                </base-input>

                <base-textarea
                  v-model="accountPayload.description"
                  :value="accountPayload.description"
                  :placeholder="t('common.placeholders.describe')"
                  @input="update()"
                >
                  <template #label>
                    {{ t("common.labels.description") }}
                  </template>
                </base-textarea>
              </div>
            </template>

            <template v-if="!account">
              <div class="empty">
                {{ t("plan.texts.signature_empty") }}
              </div>
            </template>
          </div>
        </template>
      </base-accordion>

      <base-accordion
        :open="accordions.signature"
        @open="toggleAccordion('signature', $event)"
      >
        <template #icon>
          <svg-icon icon="card-2" size="2rem" />
        </template>
        <template #title>
          {{ t("plan.labels.my_signature").toUpperCase() }}
        </template>
        <template #content>
          <div class="signatures">
            <template v-if="signature">
              <div class="info">
                <div class="line">
                  <label>{{ t("common.labels.name") }}:</label>
                  <strong>{{ signature.name }}</strong>
                </div>
                <div class="line">
                  <label>{{ t("common.labels.price") }}:</label>
                  <strong>{{ getPrice(signature.price) }}</strong>
                </div>
                <div class="line">
                  <label>{{ t("common.labels.status") }}:</label>
                  <strong>{{ statusEnum[signature.status] }}</strong>
                </div>
                <div class="line">
                  <label>{{ t("common.labels.signature") }}:</label>
                  <strong>{{ getDate(signature.created_at) }}</strong>
                </div>
                <div class="line">
                  <label>Vencimento:</label>
                  <strong>{{ getDate(signature.expiration_date) }}</strong>
                </div>
                <div class="line">
                  <label>Frequência:</label>
                  <strong>{{ frequeciesEnum[signature.frequency] }}</strong>
                </div>
              </div>
            </template>

            <template v-if="!signature">
              <div class="empty">
                {{ t("plan.texts.signature_empty") }}
              </div>
            </template>
          </div>
        </template>
      </base-accordion>

      <base-accordion
        :open="accordions.invoices"
        @open="toggleAccordion('invoices', $event)"
      >
        <template #icon>
          <svg-icon icon="card-2" size="2rem" />
        </template>
        <template #title>
          {{ t("plan.labels.my_invoices").toUpperCase() }}
        </template>
        <template #content>
          <div class="invoices">
            <template v-for="(invoice, index) in invoices" :key="index">
              <div class="item">
                <div class="number">
                  {{ index + 1 }}
                </div>
                <div class="info">
                  <div class="line">
                    <label>{{ t("common.labels.status") }}:</label>
                    <strong>{{ statusEnum[invoice.status] }}</strong>
                  </div>
                  <div class="line">
                    <label>Valor:</label>
                    <strong>
                      {{ getPrice(invoice.metadata?.price || 0) }}
                    </strong>
                  </div>
                  <div class="line">
                    <label>{{ t("common.labels.date") }}:</label>
                    <strong>{{ getDate(invoice.created_at) }}</strong>
                  </div>
                  <div class="line">
                    <label>Tipo:</label>
                    <strong>{{ typeEnum[invoice.metadata?.type] }}</strong>
                  </div>
                  <div class="line">
                    <label>Nome:</label>
                    <strong>{{ invoice.metadata?.name }}</strong>
                  </div>
                </div>
              </div>
            </template>

            <template v-if="invoices.length === 0">
              <div class="empty">
                {{ t("plan.texts.invoice_empty") }}
              </div>
            </template>
          </div>
        </template>
      </base-accordion>
    </section>

    <base-confirm
      :show="showBuyPlan"
      title="Adquirir plano"
      message="Você confirma a compra do plano?"
      @confirm="confirmBuy()"
      @cancel="cancelBuyPlan()"
    />
  </default-theme>
</template>

<style lang="scss" scoped>
.plans {
  display: flex;
  flex-direction: column;
  padding-block: 1.57rem;
  padding-inline: 1rem;
  background: #fff;

  .account {
    display: flex;
    flex-direction: column;
    padding-block-start: 0.5rem;
    padding-block-end: 1rem;

    .info {
      display: flex;
      flex-direction: column;
      gap: 1rem;
    }
  }

  .signatures {
    display: flex;
    flex-direction: column;

    .info {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;

      .line {
        display: flex;
        flex-direction: row;
        gap: 0.5rem;
      }
    }
  }

  .invoices {
    display: flex;
    flex-direction: column;

    .item {
      display: flex;
      flex-direction: row;
      align-items: flex-start;
      max-width: calc(100% - 3rem);
      padding-block: 1rem;
      padding-inline: 1.5rem;
      border-radius: 0.5rem;
      border: 1px solid var(--border);

      .number {
        width: 4rem;
        font-size: 1.5rem;
        font-weight: bold;
      }

      .info {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;

        .line {
          display: flex;
          flex-direction: row;
          gap: 0.5rem;
        }
      }
    }
  }

  .list-plans {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    gap: 1%;
    padding-block: 1rem;

    .plan {
      display: flex;
      max-width: calc(32% - 3rem);
      flex-direction: column;
      padding-block: 1rem;
      padding-inline: 1.5rem;
      border-radius: 0.5rem;
      border: 1px solid var(--border);
      margin-block-end: 1%;
    }
  }
}
</style>
